import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertTriangle,
  FiBarChart2,
  FiBook,
  FiChevronRight,
  FiClock,
  FiCpu,
  FiDatabase,
  FiHome,
  FiMessageSquare,
  FiRefreshCw,
  FiSearch,
  FiThumbsUp,
  FiToggleLeft,
  FiToggleRight,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import AdminHeader from '../components/AdminHeader';
import request from '../utils/request';

const numberFormatter = new Intl.NumberFormat('en-US');

const HOST_FILTERS = [
  { id: 'all', label: 'All Hosts' },
  { id: 'enabled', label: 'AI Enabled' },
  { id: 'disabled', label: 'AI Disabled' },
  { id: 'guidebook', label: 'Guidebook On' },
  { id: 'no-properties', label: 'No Properties' },
];

const normalizeText = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9@.\-\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const splitTerms = (value) => normalizeText(value).split(' ').filter(Boolean);

const getHostName = (host) => {
  const fullName = `${host?.firstName || ''} ${host?.lastName || ''}`.trim();
  return fullName || host?.companyName || host?.email || 'Unnamed host';
};

const getInitials = (host) => {
  const source = getHostName(host)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
  return source || 'H';
};

const getPropertyKey = (property) =>
  property?.uniqueId || property?.listingTitle || property?.label || 'property';

const getPropertyLabel = (property) => {
  const uniqueId = property?.uniqueId?.trim();
  const title = property?.listingTitle?.trim();

  if (uniqueId && title && title !== uniqueId) {
    return `${uniqueId} · ${title}`;
  }

  return title || uniqueId || 'Untitled property';
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

export default function AIConciergeScreen() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [conciergeStats, setConciergeStats] = useState(null);
  const [topQuestions, setTopQuestions] = useState([]);
  const [recentConversations, setRecentConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [togglingHostId, setTogglingHostId] = useState(null);

  const deferredSearch = useDeferredValue(search);
  const searchTerms = useMemo(() => splitTerms(deferredSearch), [deferredSearch]);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setLoadError('');

    try {
      const [statsRes, hostsRes, analyticsRes, questionsRes, convsRes] = await Promise.all([
        request.get('/studio/api/ai-stats'),
        request.get('/studio/api/hosts'),
        request.get('/studio/api/concierge-analytics').catch(() => ({ data: null })),
        request.get('/studio/api/concierge-top-questions?limit=5').catch(() => ({ data: [] })),
        request.get('/studio/api/concierge-conversations?limit=5').catch(() => ({ data: { conversations: [] } })),
      ]);

      setStats(statsRes.data || null);
      setHosts(Array.isArray(hostsRes.data) ? hostsRes.data : []);
      setConciergeStats(analyticsRes.data || null);
      setTopQuestions(Array.isArray(questionsRes.data) ? questionsRes.data : []);
      setRecentConversations(Array.isArray(convsRes.data?.conversations) ? convsRes.data.conversations : []);
    } catch (err) {
      console.error('Failed to load AI concierge data:', err);
      setLoadError('Failed to load AI Concierge data. Refresh the page and try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const searchableHosts = useMemo(() => {
    return hosts.map((host) => {
      const properties = Array.isArray(host.properties)
        ? host.properties.map((property) => ({
            ...property,
            label: getPropertyLabel(property),
            searchText: normalizeText([property?.uniqueId, property?.listingTitle].filter(Boolean).join(' ')),
          }))
        : [];

      const hostIdentitySearch = normalizeText([
        getHostName(host),
        host.email,
        host.companyName,
      ].filter(Boolean).join(' '));

      const combinedSearchText = normalizeText([
        hostIdentitySearch,
        ...properties.map((property) => property.searchText),
      ].join(' '));

      const matchingProperties = searchTerms.length
        ? properties.filter((property) => searchTerms.some((term) => property.searchText.includes(term)))
        : [];

      const matchingPropertyKeys = new Set(matchingProperties.map((property) => getPropertyKey(property)));
      const displayProperties = (
        searchTerms.length
          ? [
              ...matchingProperties,
              ...properties.filter((property) => !matchingPropertyKeys.has(getPropertyKey(property))),
            ]
          : properties
      ).slice(0, 3);

      const hostFieldMatches = searchTerms.length
        ? searchTerms.filter((term) => hostIdentitySearch.includes(term)).length
        : 0;

      const propertyMatchCount = matchingProperties.length;

      return {
        ...host,
        propertyCount: host.propertyCount ?? properties.length,
        properties,
        displayProperties,
        matchingProperties,
        hostFieldMatches,
        propertyMatchCount,
        searchText: combinedSearchText,
        matchScore: hostFieldMatches * 5 + propertyMatchCount * 3 + (host.aiConciergeEnabled ? 1 : 0),
      };
    });
  }, [hosts, searchTerms]);

  const filterCounts = useMemo(() => {
    return searchableHosts.reduce(
      (acc, host) => {
        acc.all += 1;
        if (host.aiConciergeEnabled) acc.enabled += 1;
        if (!host.aiConciergeEnabled) acc.disabled += 1;
        if (host.guidebookEnabled) acc.guidebook += 1;
        if (!host.propertyCount) acc['no-properties'] += 1;
        return acc;
      },
      {
        all: 0,
        enabled: 0,
        disabled: 0,
        guidebook: 0,
        'no-properties': 0,
      }
    );
  }, [searchableHosts]);

  const filteredHosts = useMemo(() => {
    const nextHosts = searchableHosts.filter((host) => {
      if (filterStatus === 'enabled' && !host.aiConciergeEnabled) return false;
      if (filterStatus === 'disabled' && host.aiConciergeEnabled) return false;
      if (filterStatus === 'guidebook' && !host.guidebookEnabled) return false;
      if (filterStatus === 'no-properties' && host.propertyCount > 0) return false;
      if (searchTerms.length && !searchTerms.every((term) => host.searchText.includes(term))) return false;
      return true;
    });

    return nextHosts.sort((left, right) => {
      if (searchTerms.length && right.matchScore !== left.matchScore) {
        return right.matchScore - left.matchScore;
      }
      if (right.aiConciergeEnabled !== left.aiConciergeEnabled) {
        return Number(right.aiConciergeEnabled) - Number(left.aiConciergeEnabled);
      }
      if (right.propertyCount !== left.propertyCount) {
        return right.propertyCount - left.propertyCount;
      }
      return getHostName(left).localeCompare(getHostName(right));
    });
  }, [filterStatus, searchTerms, searchableHosts]);

  const visiblePropertyCount = useMemo(
    () => filteredHosts.reduce((sum, host) => sum + (host.propertyCount || 0), 0),
    [filteredHosts]
  );

  const visiblePropertyMatches = useMemo(
    () => filteredHosts.reduce((sum, host) => sum + host.propertyMatchCount, 0),
    [filteredHosts]
  );

  const toggleHost = useCallback(async (host) => {
    setTogglingHostId(host._id);
    setLoadError('');

    try {
      const res = await request.patch(`/studio/api/hosts/${host._id}/ai-toggle`);
      const aiConciergeEnabled = Boolean(res.data.aiConciergeEnabled);

      setHosts((prevHosts) =>
        prevHosts.map((currentHost) =>
          currentHost._id === host._id
            ? { ...currentHost, aiConciergeEnabled }
            : currentHost
        )
      );

      setStats((prevStats) => {
        if (!prevStats) return prevStats;

        const activeDelta = aiConciergeEnabled ? 1 : -1;
        const propertyDelta = aiConciergeEnabled ? host.propertyCount : -host.propertyCount;
        const activeHosts = Math.max(prevStats.activeHosts + activeDelta, 0);
        const propertiesWithAI = Math.max((prevStats.propertiesWithAI || 0) + propertyDelta, 0);

        return {
          ...prevStats,
          activeHosts,
          activePercent: prevStats.totalHosts > 0
            ? Math.round((activeHosts / prevStats.totalHosts) * 100)
            : 0,
          propertiesWithAI,
        };
      });
    } catch (err) {
      console.error('Toggle failed:', err);
      setLoadError('Failed to update AI Concierge for this host.');
    } finally {
      setTogglingHostId(null);
    }
  }, []);

  const executeBulkToggle = useCallback(async (enabled) => {
    setBulkLoading(true);
    setConfirmModal(null);
    setLoadError('');

    try {
      await request.patch('/studio/api/hosts/bulk-ai-toggle', { enabled });
      await fetchData({ silent: true });
    } catch (err) {
      console.error('Bulk toggle failed:', err);
      setLoadError('Failed to update AI Concierge across hosts.');
    } finally {
      setBulkLoading(false);
    }
  }, [fetchData]);

  const statCards = stats ? [
    {
      label: 'Total Hosts',
      value: numberFormatter.format(stats.totalHosts || 0),
      detail: `${numberFormatter.format(stats.totalProperties || 0)} active properties`,
      icon: FiUsers,
      iconClassName: 'bg-sky-100 text-sky-700',
    },
    {
      label: 'AI Coverage',
      value: `${numberFormatter.format(stats.activeHosts || 0)} (${stats.activePercent || 0}%)`,
      detail: `${numberFormatter.format(stats.propertiesWithAI || 0)} properties covered`,
      icon: FiCpu,
      iconClassName: 'bg-emerald-100 text-emerald-700',
    },
    {
      label: 'Guidebook Active',
      value: `${numberFormatter.format(stats.guidebookActiveHosts || 0)} (${stats.guidebookActivePercent || 0}%)`,
      detail: 'Host-level guidebook rollout',
      icon: FiBook,
      iconClassName: 'bg-blue-100 text-blue-700',
    },
    {
      label: 'Field Mappings',
      value: `${numberFormatter.format(stats.enabledMappings || 0)} / ${numberFormatter.format(stats.totalMappings || 0)}`,
      detail: 'Mappings enabled across concierge fields',
      icon: FiDatabase,
      iconClassName: 'bg-amber-100 text-amber-700',
    },
    {
      label: 'Properties With AI',
      value: numberFormatter.format(stats.propertiesWithAI || 0),
      detail: `${numberFormatter.format(stats.totalProperties || 0)} total active properties`,
      icon: FiHome,
      iconClassName: 'bg-violet-100 text-violet-700',
    },
  ] : [];

  const conciergeStatCards = conciergeStats ? [
    {
      label: 'Total Conversations',
      value: numberFormatter.format(conciergeStats.totalConversations || 0),
      detail: 'Tracked guest threads',
      icon: FiMessageSquare,
      iconClassName: 'bg-indigo-100 text-indigo-700',
    },
    {
      label: 'Escalation Rate',
      value: `${Math.round((conciergeStats.escalationRate || 0) * 100)}%`,
      detail: 'Conversations requiring handoff',
      icon: FiAlertTriangle,
      iconClassName: 'bg-orange-100 text-orange-700',
    },
    {
      label: 'Avg Response',
      value: conciergeStats.avgResponseTimeMs > 0
        ? `${(conciergeStats.avgResponseTimeMs / 1000).toFixed(1)}s`
        : '—',
      detail: 'Median response time signal',
      icon: FiClock,
      iconClassName: 'bg-cyan-100 text-cyan-700',
    },
    {
      label: 'Feedback Score',
      value: conciergeStats.feedbackScore > 0 ? `${conciergeStats.feedbackScore}%` : '—',
      detail: 'Positive guest feedback',
      icon: FiThumbsUp,
      iconClassName: 'bg-emerald-100 text-emerald-700',
    },
  ] : [];

  return (
    <div className="flex w-full h-screen overflow-auto">
      <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
        <div className="px-10 bg-white">
          <AdminHeader heading="AI Concierge" pages="Pages / AI Concierge" />
        </div>

        <div className="px-6 py-5 overflow-auto">
          <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-medium text-zinc-500">Coverage & host rollout</p>
              <h2 className="mt-1 text-[28px] font-semibold tracking-tight text-zinc-900">
                Search hosts and properties from one concierge dataset.
              </h2>
              <p className="mt-2 text-sm text-zinc-500">
                Search by host name, email, company, property ID, or property title and manage AI enablement without per-host lookup traffic.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => fetchData({ silent: true })}
                disabled={refreshing || loading || bulkLoading}
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiRefreshCw className={joinClasses('h-4 w-4', refreshing ? 'animate-spin' : '')} />
                Refresh
              </button>
              <button
                onClick={() => navigate('/concierge-analytics')}
                className="inline-flex items-center gap-2 rounded-xl bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800"
              >
                <FiBarChart2 className="h-4 w-4" />
                Full Analytics
              </button>
            </div>
          </div>

          {loadError && (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {loadError}
            </div>
          )}

          {loading ? (
            <div className="flex min-h-[420px] items-center justify-center rounded-[28px] border border-zinc-200 bg-white">
              <div className="flex items-center gap-3 text-sm text-zinc-500">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-900" />
                Loading AI Concierge workspace...
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
                {statCards.map((card) => (
                  <div key={card.label} className="rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{card.label}</p>
                        <p className="mt-3 text-2xl font-semibold text-zinc-900">{card.value}</p>
                        <p className="mt-2 text-sm text-zinc-500">{card.detail}</p>
                      </div>
                      <div className={joinClasses('flex h-11 w-11 items-center justify-center rounded-2xl', card.iconClassName)}>
                        <card.icon className="h-5 w-5" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {conciergeStatCards.length > 0 && (
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {conciergeStatCards.map((card) => (
                    <div key={card.label} className="rounded-[24px] border border-zinc-200 bg-white p-5 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{card.label}</p>
                          <p className="mt-3 text-2xl font-semibold text-zinc-900">{card.value}</p>
                          <p className="mt-2 text-sm text-zinc-500">{card.detail}</p>
                        </div>
                        <div className={joinClasses('flex h-11 w-11 items-center justify-center rounded-2xl', card.iconClassName)}>
                          <card.icon className="h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_1fr]">
                <div className="overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm">
                  <div className="border-b border-zinc-100 px-6 py-5">
                    <div className="flex items-center gap-2">
                      <FiAlertTriangle className="h-4 w-4 text-orange-500" />
                      <h3 className="text-base font-semibold text-zinc-900">Top unanswered questions</h3>
                    </div>
                    <p className="mt-1 text-sm text-zinc-500">Questions that escalated out of the concierge flow.</p>
                  </div>

                  <div className="divide-y divide-zinc-100">
                    {topQuestions.length > 0 ? topQuestions.map((question, index) => (
                      <div key={`${question.question}-${index}`} className="flex items-start justify-between gap-4 px-6 py-4">
                        <p className="text-sm leading-6 text-zinc-700">{question.question}</p>
                        <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-semibold text-zinc-500">
                          {question.count}x
                        </span>
                      </div>
                    )) : (
                      <div className="px-6 py-10 text-sm text-zinc-400">No escalated questions yet.</div>
                    )}
                  </div>
                </div>

                <div className="overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm">
                  <div className="border-b border-zinc-100 px-6 py-5">
                    <div className="flex items-center gap-2">
                      <FiMessageSquare className="h-4 w-4 text-indigo-500" />
                      <h3 className="text-base font-semibold text-zinc-900">Recent conversations</h3>
                    </div>
                    <p className="mt-1 text-sm text-zinc-500">Latest guest activity flowing through the concierge.</p>
                  </div>

                  <div className="divide-y divide-zinc-100">
                    {recentConversations.length > 0 ? recentConversations.map((conversation) => (
                      <button
                        key={conversation._id}
                        onClick={() => navigate(`/concierge-analytics?conversation=${conversation._id}`)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-zinc-50"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-zinc-800">
                            {conversation.propertyUniqueId || 'Unknown property'}
                          </p>
                          <p className="mt-1 text-xs text-zinc-500">
                            {conversation.messageCount} messages · {formatDate(conversation.lastMessageAt)}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          {conversation.escalated && (
                            <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700">
                              Escalated
                            </span>
                          )}
                          <FiChevronRight className="h-4 w-4 text-zinc-300" />
                        </div>
                      </button>
                    )) : (
                      <div className="px-6 py-10 text-sm text-zinc-400">No conversations yet.</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm">
                <div className="border-b border-zinc-100 px-6 py-6">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="max-w-2xl">
                      <h3 className="text-lg font-semibold text-zinc-900">Host coverage workspace</h3>
                      <p className="mt-1 text-sm text-zinc-500">
                        Search across hosts and properties, review rollout state, and jump directly into host-level AI settings.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => setConfirmModal('enable')}
                        disabled={bulkLoading}
                        className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Enable All Hosts
                      </button>
                      <button
                        onClick={() => setConfirmModal('disable')}
                        disabled={bulkLoading}
                        className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Disable All Hosts
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_auto] xl:items-start">
                    <div>
                      <div className="relative">
                        <FiSearch className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                        <input
                          type="text"
                          placeholder="Search host, email, company, property ID, or property title..."
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          className="w-full rounded-2xl border border-zinc-200 bg-zinc-50 py-3 pl-11 pr-11 text-sm text-zinc-700 outline-none transition-all placeholder:text-zinc-400 focus:border-zinc-300 focus:bg-white focus:ring-4 focus:ring-zinc-100"
                        />
                        {search && (
                          <button
                            onClick={() => setSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
                            aria-label="Clear search"
                          >
                            <FiX className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {HOST_FILTERS.map((filter) => (
                          <button
                            key={filter.id}
                            onClick={() => setFilterStatus(filter.id)}
                            className={joinClasses(
                              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                              filterStatus === filter.id
                                ? 'border-zinc-900 bg-zinc-900 text-white'
                                : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50'
                            )}
                          >
                            {filter.label} ({filterCounts[filter.id] || 0})
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid min-w-full grid-cols-2 gap-3 sm:min-w-[320px]">
                      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-400">Visible Hosts</p>
                        <p className="mt-2 text-2xl font-semibold text-zinc-900">{numberFormatter.format(filteredHosts.length)}</p>
                      </div>
                      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-400">Visible Properties</p>
                        <p className="mt-2 text-2xl font-semibold text-zinc-900">{numberFormatter.format(visiblePropertyCount)}</p>
                      </div>
                      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-400">Search Terms</p>
                        <p className="mt-2 text-2xl font-semibold text-zinc-900">{numberFormatter.format(searchTerms.length)}</p>
                      </div>
                      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-400">Property Matches</p>
                        <p className="mt-2 text-2xl font-semibold text-zinc-900">{numberFormatter.format(visiblePropertyMatches)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-b border-zinc-100 px-6 py-3 text-sm text-zinc-500">
                  Showing {numberFormatter.format(filteredHosts.length)} of {numberFormatter.format(hosts.length)} hosts
                  {searchTerms.length > 0 ? ` · matching ${numberFormatter.format(visiblePropertyMatches)} properties in the current view` : ''}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-zinc-50 text-left">
                      <tr>
                        <th className="px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Host</th>
                        <th className="px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Property Coverage</th>
                        <th className="px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {filteredHosts.map((host) => (
                        <tr
                          key={host._id}
                          className="cursor-pointer transition-colors hover:bg-zinc-50"
                          onClick={() => navigate(`/ai-concierge/${host._id}`)}
                        >
                          <td className="px-6 py-4 align-top">
                            <div className="flex items-start gap-4">
                              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-zinc-100 text-sm font-semibold text-zinc-700">
                                {getInitials(host)}
                              </div>
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-sm font-semibold text-zinc-900">{getHostName(host)}</p>
                                  {host.aiConciergeEnabled && (
                                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                                      Live
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1 text-sm text-zinc-500">{host.email || 'No email on file'}</p>
                                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                                  <span>{host.companyName || 'No company assigned'}</span>
                                  <span>Created {formatDate(host.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-semibold text-zinc-600">
                                {host.propertyCount} properties
                              </span>
                              {host.propertyMatchCount > 0 && (
                                <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                                  {host.propertyMatchCount} matched
                                </span>
                              )}
                            </div>

                            {host.displayProperties.length > 0 ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {host.displayProperties.map((property) => {
                                  const isMatch = host.matchingProperties.some(
                                    (match) => getPropertyKey(match) === getPropertyKey(property)
                                  );

                                  return (
                                    <span
                                      key={`${host._id}-${getPropertyKey(property)}`}
                                      className={joinClasses(
                                        'inline-flex max-w-full rounded-full px-3 py-1 text-xs',
                                        isMatch
                                          ? 'bg-indigo-50 text-indigo-700'
                                          : 'bg-zinc-100 text-zinc-600'
                                      )}
                                    >
                                      <span className="truncate">{property.label}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="mt-3 text-sm text-zinc-400">No active properties on this host.</p>
                            )}
                          </td>

                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-col items-start gap-3">
                              <div className="flex flex-wrap gap-2">
                                <span
                                  className={joinClasses(
                                    'rounded-full px-2.5 py-1 text-xs font-semibold',
                                    host.aiConciergeEnabled
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : 'bg-zinc-100 text-zinc-500'
                                  )}
                                >
                                  {host.aiConciergeEnabled ? 'AI Enabled' : 'AI Disabled'}
                                </span>
                                <span
                                  className={joinClasses(
                                    'rounded-full px-2.5 py-1 text-xs font-semibold',
                                    host.guidebookEnabled
                                      ? 'bg-blue-50 text-blue-700'
                                      : 'bg-zinc-100 text-zinc-500'
                                  )}
                                >
                                  {host.guidebookEnabled ? 'Guidebook On' : 'Guidebook Off'}
                                </span>
                              </div>

                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleHost(host);
                                }}
                                disabled={bulkLoading || togglingHostId === host._id}
                                className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {host.aiConciergeEnabled ? (
                                  <FiToggleRight className="h-6 w-6 text-emerald-500" />
                                ) : (
                                  <FiToggleLeft className="h-6 w-6 text-zinc-300" />
                                )}
                                {togglingHostId === host._id ? 'Updating...' : 'Toggle AI'}
                              </button>
                            </div>
                          </td>

                          <td className="px-6 py-4 align-top text-right">
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/ai-concierge/${host._id}`);
                              }}
                              className="inline-flex items-center gap-1 text-sm font-medium text-zinc-700 transition-colors hover:text-zinc-900"
                            >
                              Details
                              <FiChevronRight className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}

                      {filteredHosts.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-6 py-14 text-center">
                            <p className="text-sm font-medium text-zinc-700">No hosts match the current search.</p>
                            <p className="mt-1 text-sm text-zinc-400">
                              Try a host name, company, property ID, or listing title.
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {confirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => setConfirmModal(null)}
        >
          <div
            className="mx-4 w-full max-w-sm rounded-[28px] bg-white p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-zinc-900">
              {confirmModal === 'enable' ? 'Enable AI for every host?' : 'Disable AI for every host?'}
            </h3>
            <p className="mt-2 text-sm text-zinc-500">
              {confirmModal === 'enable'
                ? `This will enable AI Concierge for all ${numberFormatter.format(hosts.length)} hosts.`
                : `This will disable AI Concierge for all ${numberFormatter.format(hosts.length)} hosts.`}
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="rounded-xl bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-200"
              >
                Cancel
              </button>
              <button
                onClick={() => executeBulkToggle(confirmModal === 'enable')}
                className={joinClasses(
                  'rounded-xl px-4 py-2 text-sm font-medium text-white transition-colors',
                  confirmModal === 'enable'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-red-600 hover:bg-red-700'
                )}
              >
                {confirmModal === 'enable' ? 'Enable All' : 'Disable All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
